import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Category as CategoryIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { ProductList } from '../../components/store';
import { getCategoryById } from '../../services/categoryService';
import { Category } from '../../types/category';

const CategoryProductsPage: React.FC = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string, subcategoryId?: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!categoryId) {
          console.error('Category ID is missing');
          setError('معرف الفئة غير موجود');
          setLoading(false);
          return;
        }

        console.log('Loading category data for ID:', categoryId);

        try {
          // تحميل بيانات الفئة الرئيسية
          const categoryData = await getCategoryById(categoryId);
          if (!categoryData) {
            console.error('Category not found for ID:', categoryId);
            setError('الفئة غير موجودة');
            setLoading(false);
            return;
          }
          setCategory(categoryData as Category);
          console.log('Category data loaded:', categoryData);

          // إذا كان هناك معرف للفئة الفرعية، قم بتحميل بياناتها
          if (subcategoryId) {
            try {
              const subcategoryData = await getCategoryById(subcategoryId);
              if (!subcategoryData) {
                console.error('Subcategory not found for ID:', subcategoryId);
                setError('الفئة الفرعية غير موجودة');
                setLoading(false);
                return;
              }
              setSubcategory(subcategoryData as Category);
              console.log('Subcategory data loaded:', subcategoryData);
            } catch (subcatError) {
              console.error('Error loading subcategory data:', subcatError);
              // نستمر بدون الفئة الفرعية
            }
          }
        } catch (catError) {
          console.error('Error loading category data:', catError);
          setError('حدث خطأ أثناء تحميل بيانات الفئة');
          setLoading(false);
          return;
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in loadCategoryData:', err);
        setError('حدث خطأ أثناء تحميل بيانات الفئة');
        setLoading(false);
      }
    };

    loadCategoryData();
  }, [categoryId, subcategoryId]);

  // تحديد العنوان والوصف المناسبين
  const title = subcategory ? subcategory.name : (category ? category.name : 'منتجات الفئة');
  const description = subcategory ? subcategory.description : (category ? category.description : '');

  // تحديد معرف الفئة المستخدم في تحميل المنتجات
  const productCategoryId = subcategoryId || categoryId;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
      {/* فتات الخبز */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link component={RouterLink} to="/" color="inherit">
          الرئيسية
        </Link>
        <Link component={RouterLink} to="/categories" color="inherit">
          الفئات
        </Link>
        {category && (
          <Link
            component={RouterLink}
            to={`/category/${category.id}`}
            color="inherit"
            sx={{ display: subcategory ? 'block' : 'none' }}
          >
            {category.name}
          </Link>
        )}
        <Typography color="text.primary">
          {title}
        </Typography>
      </Breadcrumbs>

      {/* عنوان الصفحة */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <CategoryIcon sx={{ mr: 1 }} />
          {title}
        </Typography>
        {description && (
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        )}
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* قائمة المنتجات */}
      <ProductList
        categoryId={productCategoryId}
        searchQuery=""
      />
    </Container>
  );
};

export default CategoryProductsPage;
